import { useEffect } from 'react';

import { useMessageInstanceEvent } from '../index';
import { usePlacementContext } from '../placement-context';
import type { RecommendationContext } from '../recommendation-context';
import type { DroppedMessageIdentity } from '../send-message-instance-dropped';
import { postNarrowExposureDrop } from './drop-event';
import { evaluateCohort } from './evaluate-cohort';
import type { NarrowExposureConfig, NarrowExposureDecision } from './types';

export type NarrowExposureMessage = Omit<DroppedMessageIdentity, 'recommendation'> & {
	messageInstanceId: string;
	inAppNarrowExposureExperiment?: NarrowExposureConfig | null;
	recommendationSession?: RecommendationContext;
};

export type MessagesWithExptAndChoreoDisabledOptions = {
	/** Needed only when selecting outside a placement provider. */
	placementId?: string;
	/** Display cap, independent of the backend candidate count. Omit to consider all messages. */
	maxMessagesToDisplay?: number;
};

/**
 * For placements with choreography disabled. Call after eligibility/dismissal filtering,
 * then render the returned list without further slicing.
 * Evaluated candidates use normal Statsig exposure logging. Drop events fire after commit
 * and are deduplicated by the shared drop helper. This hook never emits viewed events.
 */
export const useMessagesWithExptAndChoreoDisabled = <T extends NarrowExposureMessage>(
	messages: readonly T[],
	options: MessagesWithExptAndChoreoDisabledOptions = {},
): readonly T[] => {
	const placement = usePlacementContext();
	const placementId = options.placementId ?? placement.placementId;
	const { messageInstanceDropped } = useMessageInstanceEvent();
	const configuredCap = options.maxMessagesToDisplay;
	const cap = configuredCap === undefined ? Infinity : Math.max(0, Math.floor(configuredCap) || 0);
	const selected: T[] = [];
	const droppedMessages: {
		message: T;
		config: NarrowExposureConfig;
		decision: Exclude<NarrowExposureDecision, { outcome: 'show' }>;
	}[] = [];

	for (const message of messages) {
		if (selected.length >= cap) break;
		const config = message.inAppNarrowExposureExperiment;
		if (!config) {
			selected.push(message);
			continue;
		}
		const decision = evaluateCohort(config);
		if (decision.outcome === 'show') {
			selected.push(message);
		} else {
			droppedMessages.push({ message, config, decision });
		}
	}

	// Cohorts can change without the list changing, so check each committed render.
	useEffect(() => {
		for (const { message, config, decision } of droppedMessages) {
			postNarrowExposureDrop(
				(dropDetails) =>
					messageInstanceDropped(dropDetails, {
						...message,
						placementId,
						recommendation: message.recommendationSession ?? {},
					}),
				message.messageInstanceId,
				config,
				decision,
			);
		}
	});

	return selected;
};
